import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { funktionsArgumente, nutztEineFunktion, nutztFunktion } from "@/office/excel/formeln";
import { formulaCells, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";
import { formelAufgabe } from "../formelaufgabe";

export const lektionVerweise: Lesson = {
  id: "verweise",
  title: "Nachschlagen: SVERWEIS und XVERWEIS",
  summary: "Werte aus einer zweiten Tabelle holen, ohne sie abzutippen – die meistgebrauchte Funktion der Bürowelt.",
  durationMinutes: 14,
  learningGoals: [
    "Einen Wert aus einer Zuordnungstabelle nachschlagen",
    "Die vier Argumente des SVERWEIS sicher belegen",
    "Die Schwächen des SVERWEIS kennen und XVERWEIS einordnen",
  ],
  steps: [
    {
      id: "idee",
      title: "Die Idee: nachschlagen statt abtippen",
      explanation: [
        "In der einen Tabelle stehen Bestellungen mit Artikelnummern. In der anderen " +
          "stehen die Artikelnummern mit Bezeichnung und Preis. Wer die Preise von Hand " +
          "überträgt, tippt sich stundenlang Fehler ein – und darf beim nächsten " +
          "Preislistenwechsel von vorn beginnen.",
        "Ein Verweis macht das automatisch: Er nimmt einen Wert, sucht ihn in einer " +
          "anderen Tabelle und holt aus der gefundenen Zeile eine bestimmte Angabe " +
          "zurück. Das ist die häufigste Aufgabe in Excel überhaupt, und die Funktionen " +
          "dafür sind der Grund, warum SVERWEIS in Stellenanzeigen steht.",
        "Voraussetzung ist ein Schlüssel: ein Wert, der in beiden Tabellen vorkommt und " +
          "eindeutig ist. Artikelnummer, Kundennummer, Personalnummer. Ein Name ist " +
          "dafür schlecht geeignet – es gibt mehrere Müllers, und einer davon schreibt " +
          "sich mit ü, der andere mit ue.",
        "Damit ein Verweis funktioniert, müssen die Schlüssel exakt übereinstimmen. Die " +
          "häufigste Fehlerursache ist ein unsichtbarer Unterschied: ein Leerzeichen am " +
          "Ende, eine Zahl, die auf einer Seite als Text gespeichert ist. Bei #NV lohnt " +
          "es sich deshalb, zuerst mit =A2=D2 zu prüfen, ob die beiden Werte für Excel " +
          "wirklich gleich sind.",
      ],
      keyPoints: [
        "Ein Verweis braucht einen eindeutigen Schlüssel in beiden Tabellen.",
        "Nummern sind bessere Schlüssel als Namen.",
        "#NV heißt: nicht gefunden. Meist ein Leerzeichen oder Text statt Zahl.",
      ],
    },
    {
      id: "sverweis",
      title: "SVERWEIS in vier Argumenten",
      explanation: [
        "=SVERWEIS(Suchkriterium;Matrix;Spaltenindex;Bereich_Verweis) – vier Argumente, " +
          "von denen jedes eine eigene Fehlerquelle ist.",
        "Das Suchkriterium ist der Wert, den du suchst, meist ein Bezug auf die Zelle " +
          "mit der Artikelnummer. Die Matrix ist die Tabelle, in der gesucht wird – und " +
          "hier die wichtigste Regel: Der gesuchte Wert muss in ihrer ersten Spalte " +
          "stehen. SVERWEIS schaut ausschließlich nach rechts.",
        "Der Spaltenindex ist die Nummer der Spalte innerhalb der Matrix, aus der der " +
          "Rückgabewert kommt – gezählt ab der ersten Spalte der Matrix, nicht ab " +
          "Spalte A des Blattes. Das ist die zweite große Fehlerquelle, und die dritte " +
          "kommt gleich hinterher: Wird später eine Spalte in die Matrix eingefügt, " +
          "stimmt die Nummer nicht mehr, ohne dass Excel etwas meldet.",
        "Das vierte Argument entscheidet über genaue oder ungefähre Suche und ist " +
          "optional – was es zur gefährlichsten Voreinstellung in Excel macht. Fehlt " +
          "es, sucht Excel ungefähr und liefert den nächstkleineren Wert, wenn es den " +
          "genauen nicht findet. Bei Artikelnummern ergibt das falsche Preise ohne " +
          "jede Fehlermeldung. Schreibe deshalb immer FALSCH oder 0 als viertes " +
          "Argument – außer du willst tatsächlich in Größenklassen einordnen.",
        "Die Matrix gehört mit F4 festgehalten: $B$2:$D$50. Sonst wandert sie beim " +
          "Ausfüllen nach unten mit, und die letzten Zeilen suchen in einem Bereich, " +
          "der die gesuchten Werte gar nicht mehr enthält.",
      ],
      keyPoints: [
        "Der Suchwert muss in der ersten Spalte der Matrix stehen.",
        "Der Spaltenindex zählt ab der Matrix, nicht ab Spalte A.",
        "Immer FALSCH oder 0 als viertes Argument – sonst rät Excel.",
        "Die Matrix mit F4 festhalten, bevor die Formel ausgefüllt wird.",
      ],
      task: {
        instruction:
          "Lege eine kleine Zuordnungstabelle an – etwa Artikelnummer und Preis – und " +
          "hole mit SVERWEIS oder XVERWEIS einen Wert daraus in deine Hauptliste.",
        hint: "=SVERWEIS(A2;$G$2:$H$10;2;FALSCH). Vergiss das FALSCH am Ende nicht und halte die Matrix mit F4 fest.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort SVERWEIS oder XVERWEIS mit allen Argumenten steht.",
              true,
            );
          }

          const formeln = formulaCells(mappe);
          const verweise = formeln.filter((zelle) =>
            nutztEineFunktion(zelle.formula, ["sverweis", "xverweis", "index", "vergleich"]),
          );

          if (verweise.length === 0) {
            return fail(
              `Von ${formeln.length} Formeln schlägt keine einen Wert in einer anderen Tabelle nach.`,
              "Lege dir zuerst eine kleine Zuordnungstabelle an, zum Beispiel zwei Spalten mit " +
                "Artikelnummer und Preis. Dann: =SVERWEIS(A2;$G$2:$H$10;2;FALSCH).",
            );
          }

          const erste = verweise[0];

          // Der SVERWEIS ohne viertes Argument ist die gefährlichste
          // Voreinstellung in Excel - deshalb wird sie ausdrücklich benannt.
          const ohneGenau = verweise.filter((zelle) => {
            if (!nutztFunktion(zelle.formula, "sverweis")) return false;
            const argumente = funktionsArgumente(zelle.formula, "sverweis");
            return argumente !== null && argumente.length < 4;
          });

          if (ohneGenau.length > 0) {
            return fail(
              `In ${ohneGenau[0].address} steht „${ohneGenau[0].formula}“ – dem SVERWEIS fehlt das ` +
                "vierte Argument. Excel sucht dann ungefähr und liefert den nächstkleineren Wert, " +
                "wenn es den genauen nicht findet: falsche Ergebnisse ohne Fehlermeldung.",
              "Ergänze am Ende ein Semikolon und FALSCH, also …;2;FALSCH). Nur wenn du absichtlich in " +
                "Größenklassen einordnest, gehört dort WAHR hin.",
            );
          }

          return pass(
            `In ${erste.address} schlägt „${erste.formula}“ einen Wert nach und liefert „${erste.text}“.` +
              (verweise.length > 1 ? ` Insgesamt ${verweise.length} solcher Formeln.` : ""),
          );
        },
      },
    },
    {
      id: "xverweis",
      title: "Wo SVERWEIS an seine Grenzen kommt",
      explanation: [
        "SVERWEIS hat drei eingebaute Schwächen, und alle drei treten früher oder " +
          "später auf. Er sucht nur nach rechts, der Spaltenindex ist eine feste Zahl, " +
          "und bei Nichtfinden liefert er ein nacktes #NV.",
        "Die erste ist die ärgerlichste: Steht der Schlüssel rechts vom gesuchten Wert, " +
          "geht es nicht. Man müsste die Zuordnungstabelle umbauen – oder auf INDEX und " +
          "VERGLEICH ausweichen. Diese Kombination sucht in eine beliebige Richtung: " +
          "VERGLEICH findet die Zeilennummer, INDEX holt aus dieser Zeile den Wert einer " +
          "beliebigen Spalte. Umständlicher zu schreiben, dafür unempfindlich gegen " +
          "eingefügte Spalten.",
        "In neueren Excel-Versionen – ab Microsoft 365 und Excel 2021 – gibt es " +
          "XVERWEIS, der all das löst: Er sucht in beide Richtungen, bekommt statt eines " +
          "Spaltenindex direkt den Rückgabebereich, sucht standardmäßig genau und hat " +
          "ein eigenes Argument für den Fall, dass nichts gefunden wird. " +
          "=XVERWEIS(A2;$G$2:$G$10;$H$2:$H$10;\"unbekannt\").",
        "Der Haken ist die Verbreitung: Eine Datei mit XVERWEIS öffnet in Excel 2019 " +
          "zwar, zeigt an dieser Stelle aber #NAME?. Wenn die Datei weitergegeben wird " +
          "und du nicht weißt, welche Version am anderen Ende läuft, ist SVERWEIS mit " +
          "FALSCH weiterhin die sichere Wahl.",
      ],
      keyPoints: [
        "SVERWEIS sucht nur nach rechts; INDEX und VERGLEICH suchen in jede Richtung.",
        "XVERWEIS löst alle drei Schwächen, gibt es aber erst ab Excel 2021 / 365.",
        "Eine Datei mit XVERWEIS zeigt in älteren Versionen #NAME?.",
      ],
      task: {
        instruction:
          "Schlage mit XVERWEIS einen Wert nach und gib für den Fall, dass nichts gefunden " +
          "wird, einen eigenen Text an. (Nur in Excel 2021 und Microsoft 365.)",
        hint: "=XVERWEIS(A2;$G$2:$G$10;$H$2:$H$10;\"unbekannt\")",
        check: formelAufgabe(
          [
            {
              label: "XVERWEIS mit Ersatzwert für „nicht gefunden“",
              funktionen: ["xverweis"],
              test: (formel) => (funktionsArgumente(formel, "xverweis")?.length ?? 0) >= 4,
            },
          ],
          {
            success: "Sucht in jede Richtung und sagt verständlich, wenn nichts gefunden wird.",
            failure: "Es gibt noch keine XVERWEIS-Formel mit viertem Argument.",
            hint: "#NAME? bedeutet: Diese Excel-Version kennt XVERWEIS noch nicht – dann bleibt SVERWEIS die Wahl.",
          },
        ),
      },
    },
  ],
};
